import React from 'react'
import styled from '@emotion/styled'

import {Divider} from '@vio/atlas/Divider'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Icon, IconName} from '@vio/atlas/Icon'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'

const COVER_ROWS = [
  {cabin: 'Economy · 150', cover: 'Standard cover', price: '€200'},
  {cabin: 'Business · 30', cover: 'Premium cover', price: '€400'}
]

const PLATINUM_PERKS: {icon: IconName; title: string; subtitle: string}[] = [
  {
    icon: 'Tag',
    title: 'Member-only prices',
    subtitle: '10%+ below the public sites'
  },
  {
    icon: 'Cocktail',
    title: 'An offer at the door',
    subtitle: 'Champagne, upgrades'
  },
  {
    icon: 'CalendarCheck4',
    title: 'Flexible rates',
    subtitle: 'Free cancellation, pay later'
  },
  {
    icon: 'Headphones',
    title: 'VIP desk, 24/7',
    subtitle: 'Answered within 30 minutes'
  }
]

const Section = styled.section`
  padding-bottom: ${cssTheme.layout.spacing.s500};

  ${linariaMq.desktopXs} {
    padding-bottom: ${cssTheme.layout.spacing.s900};
  }
`

const Title = styled(Text)`
  && {
    ${cssTheme.typography.titleXL}
    padding: ${cssTheme.layout.spacing.s700} 0;
    text-align: center;
  }

  ${linariaMq.desktopXs} {
    && {
      ${cssTheme.typography.title4XL}
      padding: 0 0 ${cssTheme.layout.spacing.s800};
      text-align: start;
    }
  }
`

const Highlight = styled.span`
  color: ${cssTheme.colors.content.brand.primary};
`

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s700};

  ${linariaMq.desktopXs} {
    flex-direction: row;
    align-items: stretch;
    gap: ${cssTheme.layout.spacing.s600};
  }
`

const Card = styled.a`
  flex: 1 0 0;
  color: inherit;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s700};
  padding: ${cssTheme.layout.spacing.s700} ${cssTheme.layout.spacing.s600};
  border-radius: 24px;
  background: ${cssTheme.colors.background.neutral.secondary};
`

const PlatinumCard = styled(Card)`
  gap: ${cssTheme.layout.spacing.s800};
`

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s300};
  text-align: center;
`

const CoverBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s600};
  width: 100%;
`

const CancelledTag = styled(Tag)`
  text-align: center;
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s300};
  width: 100%;
`

const CoverRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${cssTheme.layout.spacing.s500};
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
  border: 1px solid ${cssTheme.colors.border.brand.secondary};
  border-radius: ${cssTheme.layout.radius.lg};
  background: ${cssTheme.colors.background.brand.secondary};

  ${linariaMq.desktopXs} {
    flex-direction: row;
    align-items: center;
  }
`

const CoverRowLabels = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s200};

  ${linariaMq.desktopXs} {
    flex-direction: row;
    align-items: center;
    gap: ${cssTheme.layout.spacing.s500};
  }
`

const Cabin = styled(Text)`
  ${linariaMq.desktopXs} {
    width: 120px;
  }
`

const ExposureBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s500};
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
  border-radius: ${cssTheme.layout.radius.lg};
  background: ${cssTheme.colors.background.brand.primary};
  color: ${cssTheme.colors.content.neutral.inverse};
`

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s500};
  width: 100%;
`

const FooterContent = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${cssTheme.layout.spacing.s500};
  text-align: center;

  ${linariaMq.desktopXs} {
    flex-direction: row;
    align-items: center;
    text-align: end;

    > * {
      flex: 1 0 0;
    }
  }
`

const FakeButton = styled.div`
  ${cssTheme.typography.labelM}
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: ${cssTheme.layout.spacing.s300};
  border-radius: ${cssTheme.layout.radius.md};
  color: ${cssTheme.colors.button.primary.default.content};
  background: ${cssTheme.colors.button.primary.default.background};
`

const PerksBox = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  padding: ${cssTheme.layout.spacing.s600};
  border: 1px solid transparent;
  border-radius: ${cssTheme.layout.radius.xxl};
  background:
    linear-gradient(
        127deg,
        color-mix(in srgb, ${cssTheme.colors.brand.c200} 35%, transparent) 0%,
        color-mix(in srgb, ${cssTheme.colors.brand.c100} 35%, transparent) 25%,
        color-mix(in srgb, ${cssTheme.colors.brand.c300} 35%, transparent) 50%,
        color-mix(in srgb, ${cssTheme.colors.brand.c100} 35%, transparent) 75%,
        color-mix(in srgb, ${cssTheme.colors.brand.c200} 35%, transparent) 100%
      )
      padding-box,
    linear-gradient(
        ${cssTheme.colors.background.neutral.secondary},
        ${cssTheme.colors.background.neutral.secondary}
      )
      padding-box,
    linear-gradient(
        127deg,
        ${cssTheme.colors.border.brand.secondary},
        ${cssTheme.colors.background.neutral.primary},
        ${cssTheme.colors.border.brand.secondary}
      )
      border-box;
`

const PerksTag = styled(Tag)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;

  && {
    color: ${cssTheme.colors.content.neutral.inverse};
    background: ${cssTheme.colors.background.brand.primary};
  }
`

const Perks = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${cssTheme.layout.spacing.s400};
  height: 100%;

  ${linariaMq.desktopXs} {
    grid-template-columns: 1fr 1fr;
  }
`

const Perk = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s400};
  padding: ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500};
  border: 1px solid ${cssTheme.colors.border.brand.tertiary};
  border-radius: ${cssTheme.layout.radius.xxl};
  background: ${cssTheme.colors.background.neutral.primary};
  text-align: center;
`

const PerkIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${cssTheme.colors.background.brand.tertiary};
  color: ${cssTheme.colors.content.brand.primary};
`

const PerkText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s250};
`

export const ComingSoonSection = () => (
  <Section>
    <PageWidthContainer paddingMobileSmSize="s600" paddingDesktopXsSize="s500">
      <Title variant="title4XL" tag="h2">
        <Highlight>New features</Highlight> coming soon
      </Title>
      <Cards>
        <Card
          href="https://share.vio.com/r/airline-disruption-live-share-4498xg76.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          <CardHeader>
            <Text variant="titleL">Disruption Cover</Text>
            <Text variant="bodyS" colorPath="content.neutral.secondary">
              Predictable hotel costs when flights are canceled. Passengers book
              their own room within your cap, and you&apos;re invoiced only for
              what&apos;s booked.
            </Text>
          </CardHeader>
          <CoverBody>
            <CancelledTag variant="danger" size="md">
              Canceled 21:40 · AMS → JFK · 180 passengers
            </CancelledTag>
            <Rows>
              {COVER_ROWS.map(({cabin, cover, price}) => (
                <CoverRow key={cabin}>
                  <CoverRowLabels>
                    <Cabin variant="titleS" tag="span">
                      {cabin}
                    </Cabin>
                    <Text variant="bodyS">{cover}, per room per night</Text>
                  </CoverRowLabels>
                  <Text
                    variant="titleXL"
                    tag="span"
                    colorPath="content.brand.primary"
                  >
                    {price}
                  </Text>
                </CoverRow>
              ))}
              <ExposureBox>
                <Text variant="labelS">Maximum exposure</Text>
                <Text variant="title2XL" tag="span">
                  €42,000
                </Text>
                <Text variant="bodyS">
                  150 × €200 + 30 × €400, 1 night. You are invoiced for rooms
                  actually booked, never for the cap.
                </Text>
              </ExposureBox>
            </Rows>
            <Footer>
              <Divider />
              <FooterContent>
                <Text variant="bodyS" colorPath="content.neutral.secondary">
                  One personal link per passenger, by SMS and email.
                </Text>
                <FakeButton>Generate booking links</FakeButton>
              </FooterContent>
            </Footer>
          </CoverBody>
        </Card>
        <PlatinumCard
          href="https://share.vio.com/r/platinum-hotels-prototype-dffgel9f.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          <CardHeader>
            <Text variant="titleL">Platinum Member offer</Text>
            <Text variant="bodyS" colorPath="content.neutral.secondary">
              Keep your top members&apos; hotel spend in your loyalty program,
              with member-only prices, perks, and points toward requalification.
            </Text>
          </CardHeader>
          <PerksBox>
            <PerksTag variant="brand" size="sm">
              Platinum Members offer
            </PerksTag>
            <Perks>
              {PLATINUM_PERKS.map(({icon, title, subtitle}) => (
                <Perk key={title}>
                  <PerkIcon>
                    <Icon name={icon} size="xl" />
                  </PerkIcon>
                  <PerkText>
                    <Text variant="titleM">{title}</Text>
                    <Text variant="bodyS" colorPath="content.neutral.secondary">
                      {subtitle}
                    </Text>
                  </PerkText>
                </Perk>
              ))}
            </Perks>
          </PerksBox>
        </PlatinumCard>
      </Cards>
    </PageWidthContainer>
  </Section>
)
